import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "./useAuth";
import { useIsMobile } from "../../hooks/useIsMobile";
import { usePageTitle } from "../../hooks/usePageTitle";
import { AppHeader } from "../../components/AppHeader";
import { AppFooter } from "../../components/AppFooter";
import { storeToken } from "../../utils/crossDomainAuth";

const API_BASE = import.meta.env.VITE_API_URL || "";

export function LoginPage() {
  usePageTitle("\uB85C\uADF8\uC778");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const m = useIsMobile();
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [kakaoMode, setKakaoMode] = useState(false);
  const [kakaoId, setKakaoId] = useState("");
  const [kakaoPhone, setKakaoPhone] = useState("");
  const [kakaoLoading, setKakaoLoading] = useState(false);
  const st = loginStyles(m);

  useEffect(() => {
    const kakaoToken = searchParams.get("kakao_token");
    if (kakaoToken) {
      const role = searchParams.get("role") || "broker";
      storeToken(role === "admin" ? "admin" : "partner", kakaoToken);
      localStorage.setItem("partner_token", kakaoToken);
      navigate("/dashboard", { replace: true });
      return;
    }
    const isKakao = searchParams.get("kakao");
    const kid = searchParams.get("kakaoId");
    if (isKakao && kid) {
      setKakaoMode(true);
      setKakaoId(kid);
    }
    const errParam = searchParams.get("error");
    if (errParam) setError(`로그인 오류: ${errParam}`);
  }, []);

  const startKakaoLogin = () => {
    const restApiKey = "95020a3f73c2162a1e888e9af1986d1e";
    const redirectUri = `${API_BASE}/api/auth/kakao/callback`;
    window.location.href = `https://kauth.kakao.com/oauth/authorize?client_id=${restApiKey}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code`;
  };

  const linkKakaoAccount = async () => {
    if (!kakaoPhone.trim()) { setError("전화번호를 입력해주세요."); return; }
    setKakaoLoading(true); setError("");
    try {
      const res = await fetch(`${API_BASE}/api/auth/kakao/link`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kakaoId, phone: kakaoPhone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.message || "연결 실패"); return; }
      storeToken("partner", data.token);
      localStorage.setItem("partner_token", data.token);
      navigate("/dashboard", { replace: true });
    } catch {
      setError("네트워크 오류가 발생했습니다.");
    } finally { setKakaoLoading(false); }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await login(phone.replace(/\D/g, ""), password);
      if (res.need_password_change) {
        navigate("/change-password", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (err: any) {
      setError(err.message || "로그인에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={st.page}>
      <AppHeader />
      <div style={st.container}>
      <div style={st.card}>
        <div style={st.logoArea}>
          <div style={st.lockIcon}>{"\uD83D\uDD12"}</div>
          <h1 style={st.logo}>
            <span style={{ color: "var(--lt-gold)" }}>땅</span>신뢰
          </h1>
          <div style={st.memberBadge}>PRIVATE MEMBERS</div>
          <p style={st.subtitle}>공인중개사 전용 로그인</p>
        </div>

        {/* 카카오 전화번호 연결 */}
        {kakaoMode && (
          <div style={st.form}>
            <p style={{ fontSize: 14, color: "var(--text-secondary)", marginBottom: 16, lineHeight: 1.6 }}>
              카카오 로그인 완료! 등록된 전화번호를 입력하면 계정이 연결됩니다.
            </p>
            <div style={st.field}>
              <label style={st.label}>전화번호</label>
              <input type="tel" placeholder="01012345678" value={kakaoPhone} onChange={e => setKakaoPhone(e.target.value)} style={st.input} />
            </div>
            {error && <p style={st.error}>{error}</p>}
            <button type="button" onClick={linkKakaoAccount} disabled={kakaoLoading} style={{ ...st.btn, opacity: kakaoLoading ? 0.6 : 1 }}>
              {kakaoLoading ? "연결 중..." : "계정 연결하기"}
            </button>
            <button type="button" onClick={() => { setKakaoMode(false); setError(""); window.history.replaceState({}, "", "/login"); }}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: 13, cursor: "pointer", marginTop: 12, width: "100%", textAlign: "center" as const }}>
              다른 방법으로 로그인
            </button>
          </div>
        )}

        {!kakaoMode && <>
        {/* 카카오 로그인 버튼 */}
        <div style={{ padding: "0 " + (m ? "20px" : "40px") }}>
          <button type="button" onClick={startKakaoLogin} style={{
            width: "100%", padding: "13px", border: "none", borderRadius: "var(--radius-md)",
            background: "#FEE500", color: "#191919", fontSize: 16, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
            marginBottom: 16,
          }}>
            <svg width="18" height="18" viewBox="0 0 18 18"><path d="M9 1C4.58 1 1 3.79 1 7.19c0 2.15 1.43 4.04 3.58 5.12l-.91 3.37c-.08.29.25.52.5.35l4.01-2.67c.27.03.54.04.82.04 4.42 0 8-2.79 8-6.21C17 3.79 13.42 1 9 1z" fill="#191919"/></svg>
            카카오로 시작하기
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, height: 1, background: "var(--border-medium)" }} />
            <span style={{ fontSize: 12, color: "var(--text-muted)" }}>또는</span>
            <div style={{ flex: 1, height: 1, background: "var(--border-medium)" }} />
          </div>
        </div>

        <form onSubmit={handleSubmit} style={st.form}>
          <div style={st.field}>
            <label style={st.label}>휴대폰 번호</label>
            <input
              type="tel"
              placeholder="010-0000-0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              style={st.input}
              required
            />
          </div>

          <div style={st.field}>
            <label style={st.label}>비밀번호</label>
            <input
              type="password"
              placeholder="비밀번호 입력"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={st.input}
              required
            />
          </div>

          {error && <p style={st.error}>{error}</p>}

          <button
            type="submit"
            disabled={loading}
            style={{
              ...st.btn,
              opacity: loading ? 0.6 : 1,
            }}
          >
            {loading ? "로그인 중..." : "로그인"}
          </button>
        </form>

        <p style={st.linkText}>
          <Link to="/reset-password" style={{ ...st.link, fontSize: 13 }}>
            {"\uBE44\uBC00\uBC88\uD638\uB97C \uC78A\uC73C\uC168\uB098\uC694?"}
          </Link>
        </p>
        <p style={st.linkText}>
          {"\uC544\uC9C1 \uACC4\uC815\uC774 \uC5C6\uC73C\uC2E0\uAC00\uC694?"}{" "}
          <Link to="/signup" style={st.link}>
            {"\uD68C\uC6D0\uAC00\uC785"}
          </Link>
        </p>
        </>}
      </div>
      </div>
      <AppFooter />
    </div>
  );
}

function loginStyles(m: boolean): Record<string, React.CSSProperties> {
  return {
    page: {
      minHeight: "100vh", display: "flex", flexDirection: "column" as const,
      background: "var(--bg-primary)",
    },
    container: {
      flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
      padding: m ? 16 : 20,
      background: "radial-gradient(ellipse at 50% -10%, rgba(212,168,83,0.07) 0%, transparent 65%)",
    },
    card: {
      width: "100%", maxWidth: 560,
      background: "var(--bg-card)",
      border: "1px solid var(--border-gold, rgba(212,168,83,0.2))",
      borderTop: "2px solid var(--lt-gold)",
      borderRadius: "var(--radius-xl)",
      padding: m ? "32px 20px 28px" : "44px 40px 36px",
      boxShadow: "var(--shadow-gold, 0 0 0 1px rgba(212,168,83,0.15), 0 24px 56px rgba(0,0,0,0.6))",
    },
    logoArea: { textAlign: "center" as const, marginBottom: m ? 28 : 36 },
    lockIcon: { fontSize: 30, marginBottom: 12 },
    memberBadge: {
      display: "inline-block",
      padding: "3px 12px",
      borderRadius: 20,
      border: "1px solid rgba(212,168,83,0.35)",
      color: "var(--lt-gold)",
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "1.5px",
      marginBottom: 8,
    },
    logo: { fontSize: m ? 30 : 38, fontWeight: 800, color: "var(--text-primary)", margin: "0 0 6px" },
    subtitle: { fontSize: 13, color: "var(--text-muted)", marginTop: 0 },
    form: { display: "flex", flexDirection: "column" as const, gap: 16 },
    field: { display: "flex", flexDirection: "column" as const, gap: 6 },
    label: { fontSize: 12, fontWeight: 600, color: "var(--text-muted)", letterSpacing: "0.5px", textTransform: "uppercase" as const },
    input: {
      padding: "13px 14px", borderRadius: "var(--radius-md)",
      border: "1px solid var(--border-medium)",
      background: "var(--bg-secondary)", color: "var(--text-primary)",
      fontSize: 16, outline: "none", transition: "var(--transition-fast)",
    },
    error: {
      color: "var(--lt-red)", fontSize: 13, margin: 0,
      padding: "8px 12px", background: "rgba(239,68,68,0.08)", borderRadius: "var(--radius-md)",
    },
    btn: {
      padding: "14px", borderRadius: "var(--radius-md)", border: "none",
      background: "linear-gradient(135deg, #B8972E, #D4AF37)",
      color: "#0F172A", fontSize: 16, fontWeight: 700,
      cursor: "pointer", transition: "var(--transition-fast)", marginTop: 8,
      letterSpacing: "0.3px",
    },
    linkText: { textAlign: "center" as const, fontSize: 14, color: "var(--text-muted)", marginTop: 24 },
    link: { color: "var(--lt-gold)", textDecoration: "none", fontWeight: 600 },
  };
}
